/**
 * rother3 companion — writing every imported business back out as a ZIP.
 *
 * ## Why this exists
 *
 * The import already accepts a ZIP of many audits, and `zip.js` was built to make a folder of
 * 100 hand-extracted files a single gesture. This is that same gesture running backwards.
 *
 * The case it serves: a reader accumulates businesses over weeks — one `.json` at a time, then a
 * ZIP of forty — and then wants to move the whole set to a phone, or to a colleague, or to the
 * other half of a team. Today that means re-extracting everything by hand on the new device.
 *
 * ## The one thing this must get right
 *
 * **Strip `id`.**
 *
 * The audit store is `createObjectStore('audits', { keyPath: 'id', autoIncrement: true })`, and
 * `storeAudits()` calls `store.add({ ...auditData, importedAt })`. An incoming record that already
 * carries an `id` therefore supplies an **explicit key**. If that key exists in the destination,
 * the write fails — and because the whole batch is one transaction, **one clashing row aborts
 * every row in the archive**.
 *
 * Measured, not reasoned about: exporting the stored rows verbatim and re-importing them into the
 * browser they came from fails with `AbortError` and stores nothing at all. Strip `id` and the
 * same archive imports cleanly, with the destination assigning its own keys.
 *
 * This is the same shape as the `audit` envelope check the importer runs — it is a file that
 * *looks* importable and destroys the import instead. Silently, on the other person's device.
 *
 * ## Everything else is preserved
 *
 * `importedAt` is dropped too, but only because `storeAudits()` overwrites it with the time of
 * this import — the destination's own record of when *it* received the file, which is the truth
 * for that browser. The audit body (`schemaVersion`, `type`, `data`) is written unchanged, so a
 * re-imported business has the same fields, the same read states and the same caveats as the
 * original.
 *
 * ## No manifest
 *
 * The importer reads **every** `.json` in the archive and reports anything it cannot parse as a
 * named failure. A `manifest.json` or `README.json` would therefore show up in the import report
 * as two failures the reader cannot act on. One `.json` per business, and nothing else.
 */

import { createZip } from './zip-write.js';
import { auditFields } from './audit-shape.js';

/**
 * Characters a ZIP entry name may not contain.
 *
 * An entry name is a path inside the archive. A `/` in a business name would put the audit in a
 * subdirectory, and a leading `..` would try to put it outside the archive entirely. Names come
 * from scraped Google data, so this is a real input rather than a theoretical one.
 */
const ILLEGAL_IN_NAME = /[\\/:*?"<>|\u0000-\u001f]/g;

/** Windows device names, which cannot be a filename at all. */
const RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;

/**
 * Turn a business name into a safe, readable entry name.
 *
 * @param {string} name
 * @param {string} placeId
 * @param {Set<string>} taken mutated with the name actually used
 * @returns {string} a `.json` entry name
 */
function entryName(name, placeId, taken) {
  let base = String(name ?? '').trim().replace(ILLEGAL_IN_NAME, '-').replace(/\s+/g, ' ').trim();
  base = base.replace(/[. ]+$/, ''); // Windows drops trailing dots and spaces; so does the ZIP.
  if (base === '' || RESERVED.test(base)) {base = 'business';}
  if (base.length > 60) {base = base.slice(0, 60).trim();}

  // The Place ID is part of the name, not decoration: two branches of one business share a
  // trading name and differ only by Place ID, and a ZIP where one overwrites the other would
  // lose a listing with no error anywhere.
  const id = String(placeId ?? '').trim().replace(ILLEGAL_IN_NAME, '');
  const stem = id ? `${base} [${id}]` : base;

  let candidate = `${stem}.json`;
  let n = 2;
  while (taken.has(candidate.toLowerCase())) {
    candidate = `${stem} (${n}).json`;
    n += 1;
  }
  taken.add(candidate.toLowerCase());
  return candidate;
}

/**
 * The audit body to write, with the storage keys removed.
 *
 * @param {object} record a stored record
 * @returns {object} what goes in the `.json`
 */
export function portableAudit(record) {
  const { id, importedAt, ...rest } = record ?? {};
  void id;
  void importedAt;
  return rest;
}

/**
 * Build the archive: one `.json` per stored record, newest business set included.
 *
 * Every stored record is written, **not** the deduplicated set. The older copies are the history
 * of how a listing moved, the import page shows all of them, and dropping them here would make
 * this export lossy in a way the comparison pages are not.
 *
 * @param {object[]} audits stored records, any order
 * @returns {{blob: Blob, filename: string, businessCount: number, warnings: string[]}}
 */
export function buildZipExport(audits) {
  const list = Array.isArray(audits) ? audits.filter((a) => a && typeof a === 'object') : [];
  const enc = new TextEncoder();
  const taken = new Set();
  const warnings = [];

  const entries = list.map((record, index) => {
    const f = auditFields(record);
    const name = typeof f?.businessName === 'string' && f.businessName.trim()
      ? f.businessName.trim()
      : `Business ${index + 1}`;

    if (typeof f?.placeId !== 'string' || f.placeId.trim() === '') {
      // Not fatal, but the receiving reader needs to know this row has no stable identity, or a
      // re-import of it cannot be matched against anything they already hold.
      warnings.push(name);
    }

    return {
      name: entryName(name, f?.placeId, taken),
      data: enc.encode(JSON.stringify(portableAudit(record), null, 2)),
    };
  });

  // An empty archive would be a file that imports nothing and says so silently.
  if (entries.length === 0) {
    throw new Error('Nothing to export yet. Import a listing first.');
  }

  const zip = createZip(entries);
  const stamp = new Date().toISOString().slice(0, 10);
  return {
    blob: new Blob([zip], { type: 'application/zip' }),
    filename: `rother3-businesses-${stamp}.zip`,
    businessCount: entries.length,
    warnings,
  };
}

/**
 * Group stored records by Place ID and report the repeats.
 *
 * ## Why this is computed rather than stored
 *
 * It is derived from the records themselves every time it is shown. A flag written at import time
 * goes stale the moment a business is removed, and a stale count that says "3 versions" when
 * there are 2 is worse than no count.
 *
 * ## What it returns
 *
 * - `total`: every stored record.
 * - `businesses`: distinct Place IDs (plus each unreadable one, which cannot be grouped).
 * - `repeated`: Place IDs with more than one import, with `copies` and `spanMs`.
 * - `byPlaceId`: the repeat count for each Place ID, so a card can show its own badge.
 *
 * @param {object[]} audits stored records
 * @returns {{total: number, businesses: number, repeated: object[], byPlaceId: Map<string, number>}}
 */
export function summariseRepeats(audits) {
  const list = Array.isArray(audits) ? audits.filter((a) => a && typeof a === 'object') : [];
  const groups = new Map();
  let unidentifiable = 0;

  for (const record of list) {
    const f = auditFields(record);
    const key = typeof f?.placeId === 'string' ? f.placeId.trim() : '';
    if (key === '') {unidentifiable += 1; continue;}

    const at = Date.parse(record.importedAt);
    const when = Number.isFinite(at) ? at : null;
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, { placeId: key, copies: 1, name: f?.businessName ?? '', first: when, last: when });
      continue;
    }
    existing.copies += 1;
    if (when !== null) {
      existing.first = existing.first === null ? when : Math.min(existing.first, when);
      existing.last = existing.last === null ? when : Math.max(existing.last, when);
    }
  }

  const repeated = [...groups.values()]
    .filter((g) => g.copies > 1)
    .sort((a, b) => b.copies - a.copies);

  return {
    total: list.length,
    businesses: groups.size + unidentifiable,
    repeated,
    unidentifiable,
    byPlaceId: new Map([...groups].map(([k, g]) => [k, g.copies])),
  };
}